import React, { memo, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { styled } from '@linaria/react';
import ContentWrapper from '@vio/atlas/helpers/ContentWrapper';
import { Flex } from '@vio/atlas/helpers/Flex';
import { cssTheme } from '@vio/atlas/themes';
import { getIsSearchPending } from 'modules/sapiSearch/selectors';
import { FilterUrlParam } from 'modules/search/types';
import { FacetType } from 'types/Search';
import { filterMessages } from '../filterMessages';
import { getValidFacetGroups, getValidFacets } from '../utils';
import { FacetFilterContentLoader } from './FacetFilterContentLoader';
import { FacetsRenderCard } from './FacetsRenderCard';
import { FacetsRenderGroup } from './FacetsRenderGroup';
import { FacetsRenderList } from './FacetsRenderList';
import { ColumnType, FacetGroupItem, RenderType, ShowMoreRenderType } from './types';
const LENGTH_FOR_POPULAR_LIST_DISPLAY = 9;
const _exp = /*#__PURE__*/() => Flex.Row;
const _exp2 = /*#__PURE__*/() => ({
  maxHeight = 260
}) => maxHeight;
const _exp4 = /*#__PURE__*/() => ({
  renderType
}) => cssTheme.layout.spacing[renderType === RenderType.Card ? 's100' : 's300'];
const _exp5 = /*#__PURE__*/() => ({
  renderType
}) => cssTheme.layout.spacing[renderType === RenderType.Card ? 's400' : 's300'];
const ScrollableListWrapper = /*#__PURE__*/styled(_exp())({
  name: "ScrollableListWrapper",
  class: "sz0w01a",
  propsAsIs: true,
  vars: {
    "sz0w01a-0": [_exp2(), "px"],
    "sz0w01a-1": [_exp4()],
    "sz0w01a-2": [_exp5()]
  }
});
const _exp8 = /*#__PURE__*/() => Flex.Row;
const _exp9 = /*#__PURE__*/() => ({
  maxHeight
}) => maxHeight ? `${maxHeight}px` : 'auto';
const ListWrapper = /*#__PURE__*/styled(_exp8())({
  name: "ListWrapper",
  class: "llaaaq6",
  propsAsIs: true,
  vars: {
    "llaaaq6-0": [_exp9()]
  }
});
export interface FacetFilterProps {
  /** The list of facets for this filter */
  facets: FacetType[];
  /** Group of facets to be displayed */
  facetGroups: FacetGroupItem[];
  /** The filters applied in local state */
  filters: FilterUrlParam;
  /** The filters applied from the search params */
  appliedFilters: FilterUrlParam;
  /** The search param name for this filter */
  searchParamName: string;
  /** The callback to render the label */
  getLabel: (T: string) => string | unknown;
  /** The callback fired on filter change */
  onChange: (filterName: string, filterValue: string | null | undefined) => void;
  /** The callback fired on set multi filters */
  setMultiFilters?: (filterName: string, filterValues: (string | number)[]) => void;
  /** The callback fired on remove multi filters */
  removeMultiFilters?: (filterName: string, filterValues: (string | number)[]) => void;
  /** If the list should be scrollable */
  isScrollable?: boolean;
  /** The number of columns to render */
  columns?: ColumnType;
  /** The limit of filter results - if there is a limit the list will be expandable */
  limit?: number;
  /** Showing show more in different design */
  showMoreRenderType?: ShowMoreRenderType;
  /** The message to render in the button when the list is contracted */
  contractedMessage?: string;
  /** Scrollable container max height */
  containerMaxHeight?: number;
  /** Showing facets in different  design */
  renderType?: RenderType;
}
const FacetFilterComponent: React.FC<FacetFilterProps> = props => {
  const {
    appliedFilters,
    facets,
    facetGroups,
    isScrollable = false,
    containerMaxHeight,
    renderType = RenderType.List
  } = props;
  const isSearchPending = useSelector(getIsSearchPending);
  const {
    formatMessage
  } = useIntl();
  const validFacets = useMemo(() => getValidFacets(facets, appliedFilters), [facets, appliedFilters]);
  if (validFacets.length === 0) {
    if (isSearchPending) {
      return <FacetFilterContentLoader renderType={renderType} />;
    }
    return <ContentWrapper paddingBottom="s600">
        {formatMessage(filterMessages.noFiltersAvailable)}
      </ContentWrapper>;
  }

  // Check if the facets are valid and group them by group type
  const groups = getValidFacetGroups(validFacets, facetGroups);
  const popularFacets = groups?.popular;
  const popularIsVisible = validFacets.length > LENGTH_FOR_POPULAR_LIST_DISPLAY && popularFacets?.length > 0;
  const popularCardsIsVisible = renderType === RenderType.Card && validFacets.length > LENGTH_FOR_POPULAR_LIST_DISPLAY && popularFacets?.length > 0;
  if (renderType === RenderType.Group) {
    if (isScrollable) {
      return <ScrollableListWrapper maxHeight={containerMaxHeight} renderType={renderType}>
          <FacetsRenderGroup {...props} groups={groups} />
        </ScrollableListWrapper>;
    }
    return <FacetsRenderGroup {...props} groups={groups} />;
  }
  if (popularCardsIsVisible) {
    if (isScrollable) {
      return <ScrollableListWrapper maxHeight={containerMaxHeight} renderType={renderType}>
          <FacetsRenderCard popularFacets={popularFacets} validFacets={validFacets} {...props} />
        </ScrollableListWrapper>;
    }
    return <FacetsRenderCard popularFacets={popularFacets} validFacets={validFacets} {...props} />;
  }
  if (isScrollable) {
    return <ScrollableListWrapper maxHeight={containerMaxHeight} renderType={renderType}>
        <FacetsRenderList popularIsVisible={popularIsVisible} popularFacets={popularFacets} validFacets={validFacets} {...props} />
      </ScrollableListWrapper>;
  }
  return <ListWrapper maxHeight={containerMaxHeight}>
      <FacetsRenderList popularIsVisible={popularIsVisible} popularFacets={popularFacets} validFacets={validFacets} {...props} />
    </ListWrapper>;
};
export const FacetFilter = memo(FacetFilterComponent);

require("./FacetFilter.wyw-in-js.css!=!../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./FacetFilter.tsx");